import { ReactNode, useEffect } from "react";
import AlongLogo from "components/AlongLogo";
import Footer from "components/Footer";
import { useLocation } from "react-router-dom";
import styled from "styled-components/macro";
import { ExternalLink as ExternalLinkIcon } from "ui/Icons";
import Link from "ui/Link";
import { theme } from "ui/theme";
import Typography from "ui/Typography";
import { AnalyticsProvider } from "utils/analytics/AnalyticsContext";
import {
  ActionType,
  EventName,
  SurfaceType,
} from "utils/analytics/analyticsConstants";
import { HelpLinks } from "utils/helpLinks";

import TopCornerSplat from "images/top-corner-splat.png";
import TopEdgeEllipse from "images/top-edge-ellipse.png";

const $PageWrapper = styled.div`
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;
  z-index: ${theme.zIndexBelow};
`;

const $TopEdgeEllipse = styled.img.attrs({
  src: TopEdgeEllipse,
  alt: "",
})`
  position: absolute;
  top: 0;
  width: 100%;
  z-index: ${theme.zIndexBelow};
`;

const $TopCornerSplat = styled.img.attrs({
  src: TopCornerSplat,
  alt: "",
})`
  position: absolute;
  right: 0;
  z-index: ${theme.zIndexBelow};

  @media screen and (${theme.BreakpointTabletDown}) {
    display: none;
  }
`;

const $AlongLogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 3.5rem;
`;

const $AlongLogo = styled(AlongLogo).attrs({
  variant: "bulb-mint",
})`
  height: 4rem;
`;

const $ContentContainer = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const $HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 5rem 2rem 0rem;
`;

export const $PageTitle = styled.span`
  font-family: ${theme.FontFamilyBodySerif};
  font-size: 4rem;
  line-height: 4.5rem;
`;

const $LastUpdated = styled.span`
  color: ${theme.ColorSpace300};
  margin-top: 1.5rem;
`;

const $GraphicIllustration = styled.img.attrs((props) => ({
  src: props.src,
  alt: props.alt,
}))`
  srcset: ${(props) => props.srcSet} 2x;
  display: block;
  margin-top: 0.5rem;
  height: 10rem;
`;

export const $TextContainer = styled.div`
  max-width: 50rem;
  margin: 0rem 2rem;
  text-align: left;
  overflow-wrap: normal;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const $OverviewText = styled(Typography)`
  font-family: ${theme.FontFamilyHeading};
`;

export const $SectionTitle = styled(Typography)`
  font-family: ${theme.FontFamilyHeading};
  font-weight: 300;
  margin-top: 4rem;
  margin-bottom: 1.875rem;
`;

export const $SubsectionTitle = styled(Typography)`
  margin-top: 2rem;
  margin-bottom: 1.25rem;
`;

export const $Paragraph = styled.p`
  margin-bottom: 1.875rem;
`;

export const $UnorderedList = styled.ul`
  margin-bottom: 1.875rem;
`;

export const $ListItem = styled.li`
  margin: 1rem 0rem;
`;

export const $SummaryBox = styled.blockquote`
  background-color: ${theme.ColorSky};
  position: relative;
  padding: 1.875rem;
  margin-bottom: 2rem;
  border-radius: 0.5625rem;

  *:last-child {
    margin-bottom: 0;
  }
`;

export const $SummaryBoxTitle = styled(Typography)`
  font-family: ${theme.FontFamilyHeading};
  font-weight: ${theme.FontWeightBold};
  margin-bottom: 1.875rem;
`;

export const $UnderlinedText = styled.span`
  text-decoration: underline;
`;

interface LinkProps {
  linkText?: string;
  analyticsName?: string;
  href?: string;
  title?: string;
  displayExternalLinkIcon?: boolean;
}

export const $SupportAtAlongEmailLink = ({
  linkText = "support@along.org",
}: LinkProps) => {
  return (
    <Link
      analyticsName={EventName.PolicyPages.CLICKED_EMAIL_SUPPORT_LINK}
      href="mailto:support@along.org"
    >
      {linkText}
    </Link>
  );
};

export const $ReportAtAlongEmailLink = ({
  linkText = "report@along.org",
}: LinkProps) => {
  return (
    <Link
      analyticsName={EventName.PolicyPages.CLICKED_EMAIL_REPORT_LINK}
      href="mailto:report@along.org"
    >
      {linkText}
    </Link>
  );
};

export const $PrivacyAtAlongEmailLink = ({
  linkText = "privacy@along.org",
}: LinkProps) => {
  return (
    <Link
      analyticsName={EventName.PolicyPages.CLICKED_EMAIL_PRIVACY_LINK}
      href="mailto:privacy@along.org"
    >
      {linkText}
    </Link>
  );
};

export const $SecurityAtAlongEmailLink = ({
  linkText = "security@along.org",
}: LinkProps) => {
  return (
    <Link
      analyticsName={EventName.PolicyPages.CLICKED_EMAIL_SECURITY_LINK}
      href="mailto:security@along.org"
    >
      {linkText}
    </Link>
  );
};

export const $LegalAtAlongEmailLink = ({
  linkText = "legal@along.org",
}: LinkProps) => {
  return (
    <Link
      analyticsName={EventName.PolicyPages.CLICKED_EMAIL_LEGAL_LINK}
      href="mailto:legal@along.org"
    >
      {linkText}
    </Link>
  );
};

export const $PrivacyPolicyLink = ({
  linkText = "Privacy Policy",
}: LinkProps) => {
  return (
    <Link
      analyticsName={EventName.PolicyPages.CLICKED_PRIVACY_POLICY_LINK}
      href="/privacy-policy"
      opensInNewTab
    >
      {linkText}
    </Link>
  );
};

export const $UserAgreementLink = ({
  linkText = "User Agreement",
}: LinkProps) => {
  return (
    <Link
      analyticsName={EventName.PolicyPages.CLICKED_USER_AGREEMENT_LINK}
      href="/user-agreement"
      opensInNewTab
    >
      {linkText}
    </Link>
  );
};

export const $CodeOfConductLink = ({
  linkText = "Code of Conduct",
  title = "Code of Conduct",
}: LinkProps) => {
  return (
    <Link
      analyticsName={EventName.PolicyPages.CLICKED_CODE_OF_CONDUCT_LINK}
      href="/code-of-conduct"
      opensInNewTab
      title={title}
    >
      {linkText}
    </Link>
  );
};

const $ExternalLinkIconWrapper = styled.span`
  // We're using em units for this icon's styling because so it scales
  // with the link's font size.

  margin-left: 0.1em;

  svg {
    width: 0.8em;
    height: 0.8em;
  }
`;

const $Link = styled.a`
  color: inherit;
  text-decoration: underline;

  &:active,
  &:focus,
  &:hover {
    color: ${theme.ColorBlurple500};
  }

  // Icons should inherit the text color by default.
  path {
    fill: currentColor;
  }
`;

export const $ExternalLink = ({
  href,
  linkText,
  title,
  displayExternalLinkIcon = true,
}: LinkProps) => {
  const handleLinkClick = () => {
    window.analytics.track(ActionType.CLICK_LINK, {
      target: href,
      text: linkText,
    });
  };

  return (
    <$Link
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={handleLinkClick}
      title={title}
    >
      {linkText}
      {displayExternalLinkIcon && (
        <$ExternalLinkIconWrapper>
          <ExternalLinkIcon role="img" title="Opens in a new tab." />
        </$ExternalLinkIconWrapper>
      )}
    </$Link>
  );
};

const $SummaryOfUpdatesLink = ({
  linkText = "updates",
  title = "Summary of Updates",
}: LinkProps) => {
  return (
    <$ExternalLink
      href={
        HelpLinks.SUMMARY_OF_UPDATES_TO_USER_AGREEMENT_PRIVACY_POLICY_CODE_OF_CONDUCT
      }
      linkText={linkText}
      title={title}
    />
  );
};

export const $SecurityWhitepaperLink = ({
  linkText = "Security Whitepaper",
}: LinkProps) => {
  return (
    <$ExternalLink href={HelpLinks.SECURITY_WHITEPAPER} linkText={linkText} />
  );
};

export const $DMCANotificationGuidelinesLink = ({
  linkText = "DMCA Notification Guidelines",
}: LinkProps) => {
  return (
    <$ExternalLink
      href={
        HelpLinks.DIGITAL_MILLENNIUM_COPYRIGHT_ACT_DMCA_NOTIFICATION_GUIDELINES
      }
      linkText={linkText}
    />
  );
};

export const $CookiePolicyLink = ({
  linkText = "Cookie Policy",
}: LinkProps) => {
  return <$ExternalLink href={HelpLinks.COOKIE_POLICY} linkText={linkText} />;
};

export const $ThirdPartyServiceProvidersLink = ({
  linkText = "Third-Party Service Providers",
}: LinkProps) => {
  return (
    <$ExternalLink
      href={HelpLinks.THIRD_PARTY_SERVICE_PROVIDERS}
      linkText={linkText}
    />
  );
};

interface PolicyLayoutProps {
  children: ReactNode;
  pageTitle?: ReactNode;
  lastUpdatedDate?: string;
  src?: string;
  srcSet?: string;
  alt?: string;
  userType?: "teacher" | "student";
}

export default function PolicyLayout({
  children,
  pageTitle,
  lastUpdatedDate,
  src,
  srcSet,
  alt,
  userType = "teacher",
}: PolicyLayoutProps) {
  const location = useLocation();

  useEffect(() => {
    // for scrolling to specific elements on a policy page
    if (location.hash) {
      // use setImmediate to ensure correct scrolling behavior after page load completes
      setImmediate(() => {
        const element = document.querySelector(location.hash);
        element?.scrollIntoView();
      });
    }
  }, [location]);

  return (
    <AnalyticsProvider surface={SurfaceType.POLICY_PAGES}>
      <$PageWrapper>
        <$TopEdgeEllipse />
        <$TopCornerSplat />
        <$AlongLogoContainer>
          <Link
            analyticsName={EventName.PolicyPages.CLICKED_HOME_LINK}
            href="/"
            noExternalLinkIcon
            title="Go to Along product homepage"
          >
            <$AlongLogo />
          </Link>
        </$AlongLogoContainer>

        <$HeaderContainer>
          {pageTitle}
          <$LastUpdated>
            Last Updated: {lastUpdatedDate} (summary of{" "}
            <$SummaryOfUpdatesLink />)
          </$LastUpdated>
          <$GraphicIllustration src={src} srcSet={srcSet} alt={alt} />
        </$HeaderContainer>

        <$ContentContainer>{children}</$ContentContainer>

        <Footer userType={userType} />
      </$PageWrapper>
    </AnalyticsProvider>
  );
}
